import { useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';

import { ContextMenu } from '@/components';
import {
  BOARD_STATUS_LABEL,
  BOARD_STATUS_OPTIONS,
  type BoardStatus,
} from '@/pages/Crew/crewRoster';

import s from './CrewStatusPicker.module.scss';

type CrewStatusPickerProps = {
  status: BoardStatus;
  /** Whose row this is — the control's accessible name says so. */
  personName: string;
  editable: boolean;
  onSet: (status: BoardStatus) => void;
};

/**
 * **Where a crew member stood on this day: on board, or on leave.**
 *
 * The crew roster's chip, in the daily log — same two statuses, same labels,
 * same colours, so a status means one thing across both screens. What it writes
 * is not the same thing: this sets the value on *this log entry* and nothing
 * else. Crew Management seeds the entry (see `useLogCrew`) and stays where it
 * is, because a log is a record of a day and presence is a fact about now.
 *
 * It replaced the old four-value duty select — On Duty, Off Duty, Watch, Leave —
 * which said something the crew record already answers, in a vocabulary of its
 * own.
 */
export function CrewStatusPicker({ status, personName, editable, onSet }: CrewStatusPickerProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const label = BOARD_STATUS_LABEL[status];
  const classes = `${s.chip} ${status === 'on_board' ? s.onBoard : s.onLeave}`;

  if (!editable) {
    return <span className={classes}>{label}</span>;
  }

  return (
    <>
      <button
        type="button"
        className={`${classes} ${s.button}`}
        aria-haspopup="menu"
        aria-expanded={!!anchor}
        aria-label={`${personName} — ${label}, change`}
        onClick={(e) => {
          const target = e.currentTarget;
          setAnchor((prev) => (prev ? null : target));
        }}
      >
        {label}
        <FiChevronDown aria-hidden="true" />
      </button>

      <ContextMenu
        isOpen={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        items={BOARD_STATUS_OPTIONS.map((option) => ({
          label: option.label,
          icon: option.icon,
          onClick: () => {
            onSet(option.value);
            setAnchor(null);
          },
        }))}
      />
    </>
  );
}
